import { Marked } from "marked"

import { mintlifyIconHtml } from "@productlane/lib/src/tiptap/extensions/mintlify/icon-html"
import { unwrapBlockImages } from "@productlane/lib/src/tiptap/helpers/unwrap-block-images"
import { escapePanelAttribute } from "@productlane/lib/src/tiptap/panel-markdown"

import { MDX_BLOCK_READERS } from "./mdx"
import { renderBadgeInline } from "./mdx/badge"
import { renderIconInline } from "./mdx/icon"
import { renderTooltipInline } from "./mdx/tooltip"

// Kept free of Tiptap and turndown so the portal can render a description with
// the exact HTML the dashboard editor is seeded with.

const CHECKBOX_LIST =
	/<ul>\n?((?:<li><input[^>]*type="checkbox"[^>]*>[\s\S]*?<\/li>\n?)+)<\/ul>/gi
const CHECKBOX_ITEM = /<li><input([^>]*)>\s*([\s\S]*?)<\/li>/gi

// marked writes a GFM checklist as a plain <ul> of disabled inputs, which Tiptap
// reads as an ordinary bullet list, losing the checkbox on the way back out.
function toTaskLists(html: string): string {
	return html.replace(CHECKBOX_LIST, (_list, items: string) => {
		const taskItems = items.replace(
			CHECKBOX_ITEM,
			(_item, attributes: string, text: string) =>
				`<li data-type="taskItem" data-checked="${/\bchecked\b/.test(attributes)}"><p>${text.trim()}</p></li>`,
		)
		return `<ul data-type="taskList">${taskItems}</ul>`
	})
}

const PANEL_PLACEHOLDER = /<!--pl-panel-(\d+)-->/g
const TABS_BLOCK = /<Tabs>([\s\S]*?)<\/Tabs>/g
const TAB_BLOCK = /<Tab\b([^>]*)>([\s\S]*?)<\/Tab>/g
const CODE_GROUP_BLOCK = /<CodeGroup>([\s\S]*?)<\/CodeGroup>/g
const CARD_GROUP_BLOCK = /<(CardGroup|Columns)\b([^>]*)>([\s\S]*?)<\/\1>/g
const CARD_BLOCK = /<Card\b([^>]*?)(?:\/>|>([\s\S]*?)<\/Card>)/g
const FENCE_BLOCK = /```([^\n]*)\n([\s\S]*?)```/g
const MAX_PANEL_DEPTH = 3

const readAttribute = (attributes: string, name: string): string | null =>
	new RegExp(`${name}="([^"]*)"`).exec(attributes)?.[1] ?? null

// The inner markdown of a tab is indented under its tag, and four of those
// spaces would otherwise read as a code block.
function dedent(block: string): string {
	const lines = block.replace(/^\n+/, "").replace(/\s+$/, "").split("\n")
	const indents = lines
		.filter((line) => line.trim())
		.map((line) => /^[ \t]*/.exec(line)?.[0].length ?? 0)
	const shortest = indents.length ? Math.min(...indents) : 0
	return lines.map((line) => line.slice(shortest)).join("\n")
}

// Mintlify writes the label of a code group as the fence's filename, so
// ```ts client.ts carries both the language and the tab title.
function fenceToHtml(info: string, code: string): string {
	const [language, ...rest] = info.trim().split(/\s+/)
	const title = rest.join(" ")
	const attributes = [
		language ? ` data-language="${escapePanelAttribute(language)}"` : "",
		title ? ` data-title="${escapePanelAttribute(title)}"` : "",
	].join("")
	const classAttribute = language
		? ` class="language-${escapePanelAttribute(language)}"`
		: ""
	return `<pre${attributes}${classAttribute}><code${classAttribute}>${code
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/\n$/, "")}</code></pre>`
}

// Own instance so the fence renderer stays scoped to article bodies: the
// default one drops the title next to the language and the `data-language`
// the reader-side highlighter reads.
const markdownParser = new Marked({
	breaks: true,
	gfm: true,
	renderer: {
		code: ({ text, lang }) => fenceToHtml(lang ?? "", `${text}\n`),
	},
})

function panelHtml(source: string, depth: number): string {
	let html = ""
	for (const match of source.matchAll(TAB_BLOCK)) {
		const attributes = match[1] ?? ""
		const title = readAttribute(attributes, "title") ?? "Tab"
		const body = parseMarkdown(dedent(match[2] ?? ""), depth + 1)
		html += `<div data-type="tab" data-title="${escapePanelAttribute(
			title,
		)}">${body}</div>`
	}
	return `<div data-type="tabs" data-panel-group="tabs" data-active="1">${html}</div>`
}

const escapeText = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

function cardHtml(attributes: string, body: string, depth: number): string {
	const title = escapeText(readAttribute(attributes, "title") ?? "")
	const href = readAttribute(attributes, "href")
	const icon = readAttribute(attributes, "icon")
	const heading = `${icon ? mintlifyIconHtml(icon, { size: 24 }) : ""}${
		href ? `<a href="${escapePanelAttribute(href)}">${title}</a>` : title
	}`
	const content = body.trim() ? parseMarkdown(dedent(body), depth + 1) : ""
	return `<div data-type="card"><p data-card-title>${heading}</p>${content}</div>`
}

function cardGroupHtml(
	attributes: string,
	source: string,
	depth: number,
): string {
	const cols = /cols=\{?"?(\d)/.exec(attributes)?.[1] ?? "2"
	let html = ""
	for (const match of source.matchAll(CARD_BLOCK)) {
		html += cardHtml(match[1] ?? "", match[2] ?? "", depth)
	}
	return `<div data-type="card-group" data-cols="${cols}">${html}</div>`
}

function codeGroupHtml(source: string): string {
	let html = ""
	for (const match of source.matchAll(FENCE_BLOCK)) {
		html += fenceToHtml(match[1] ?? "", match[2] ?? "")
	}
	return `<div data-type="code-group" data-panel-group="code-group" data-active="1">${html}</div>`
}

// Each block leaves a comment behind, because marked ends a raw HTML block at
// the first blank line and would split the group across paragraphs.
function extractPanels(
	markdown: string,
	depth: number,
): { markdown: string; blocks: string[] } {
	const blocks: string[] = []
	if (depth >= MAX_PANEL_DEPTH) return { markdown, blocks }

	let source = markdown
	for (const match of [...source.matchAll(TABS_BLOCK)]) {
		blocks.push(panelHtml(match[1] ?? "", depth))
		source = source.replace(match[0], `<!--pl-panel-${blocks.length - 1}-->`)
	}
	for (const match of [...source.matchAll(CODE_GROUP_BLOCK)]) {
		blocks.push(codeGroupHtml(match[1] ?? ""))
		source = source.replace(match[0], `<!--pl-panel-${blocks.length - 1}-->`)
	}
	for (const match of [...source.matchAll(CARD_GROUP_BLOCK)]) {
		// A Columns of tiles belongs to the tile reader.
		if (/<Tile\b/.test(match[3] ?? "")) continue
		blocks.push(cardGroupHtml(match[2] ?? "", match[3] ?? "", depth))
		source = source.replace(match[0], `<!--pl-panel-${blocks.length - 1}-->`)
	}
	// A card on its own is a one-column group, the same full-width box.
	for (const match of [...source.matchAll(CARD_BLOCK)]) {
		blocks.push(
			`<div data-type="card-group" data-cols="1">${cardHtml(match[1] ?? "", match[2] ?? "", depth)}</div>`,
		)
		source = source.replace(match[0], `<!--pl-panel-${blocks.length - 1}-->`)
	}
	for (const reader of MDX_BLOCK_READERS) {
		for (const match of [...source.matchAll(reader.pattern)]) {
			blocks.push(
				reader.render(match, (body) => parseMarkdown(dedent(body), depth + 1)),
			)
			source = source.replace(match[0], `<!--pl-panel-${blocks.length - 1}-->`)
		}
	}
	return { markdown: source, blocks }
}

// marked puts every image inside a paragraph, where the block image node has
// nowhere to live, so each one is dropped the first time the HTML is parsed.
function parseMarkdown(markdown: string, depth = 0): string {
	const { markdown: source, blocks } = extractPanels(markdown, depth)
	const html = toTaskLists(
		unwrapBlockImages(markdownParser.parse(source, { async: false })),
	)
	return html.replace(
		PANEL_PLACEHOLDER,
		(placeholder, index: string) => blocks[Number(index)] ?? placeholder,
	)
}

export function renderMarkdownHtml(markdown: string): string {
	return renderIconInline(
		renderTooltipInline(renderBadgeInline(parseMarkdown(markdown))),
	)
}
